import i18n from '@scripts/i18n-setup'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import {addressFinderErrorCodes, CITY_TYPES} from '@libs/utils/const'
import {
  PlaceAutocompletePrediction,
  PlaceAutocompleteResponse, PlaceResult,
} from '@store/types/google'
import {FormattedAddress} from '@interfaces/checkout'
import {fetchSuggestions, fetchPlaceDetails} from '@libs/services/googlePlacesService'

export enum FilterType {
  BOOLEAN = 'boolean',
  MULTISELECT = 'multiselect',
}

export interface FilterOption {
  value: string | number
  label: string
}


export type FilterValue = boolean | string[] | undefined

export interface BaseFilter<T extends FilterValue = FilterValue> {
  id: string
  label: string
  type: FilterType
  value: T
  defaultValue: T
  storageKey?: string
  options?: FilterOption[]
  minPlaceholder?: string
  maxPlaceholder?: string
  applyFilter: (item: any, filterValue: any) => boolean;
  results:number
  resultItems: readonly any[]
}

export interface BooleanFilter extends BaseFilter<boolean> {
  type: FilterType.BOOLEAN

}

export interface MultiselectFilter extends BaseFilter<string[]> {
  type: FilterType.MULTISELECT
  options: FilterOption[]
}

export type FinInStoreFilter = BooleanFilter | MultiselectFilter


export function getAvailableFilters() {
  return {
    AVAILABILITY: {
      id: 'inStoreStock',
      label: getLabel('sl_in_store_availability', 'in store availability'),
      type: FilterType.BOOLEAN as const,
      defaultValue: false,
      value: false,
      storageKey: 'store_availability_filter',
      applyFilter: (store: any,filterValue:any) => {
        return filterValue ? store.inventory > 0 : true
      },
      results: 0,
      resultItems:[]
    }
  };
}

/*type AvailableFilters = typeof AVAILABLE_FILTERS
type FilterId = keyof AvailableFilters*/

/**
 * Initialize filters with saved values or defaults
 */
export function initializeFilters<T extends Record<string, FinInStoreFilter>>(
  filtersConfig: T
): FinInStoreFilter[] {
  try {

    return Object.values(filtersConfig).map(filter => ({
      ...filter,
      value: filter.storageKey
        ? JSON.parse(localStorage.getItem(filter.storageKey) || 'null') ?? filter.defaultValue
        : filter.defaultValue
    }))
  } catch (e) {
    return []
  }

}

/**
 * Get active filters
 * @param items
 * @param filters Array of filters to check
 * @returns Array of active filters
 */
export function applyFilters<T>(items: T[], filters: FinInStoreFilter[]): T[] {
  try {
    if (!items || !filters || filters.length === 0) {
      return [...items];
    }

    const activeFilters = getActiveFilters(filters)

    if (activeFilters.length === 0) {
      return [...items];
    }

    activeFilters.forEach(filter => {
      filter.resultItems = []
    })
    //
    return items.filter((item,itemIndex) => {
      return activeFilters.every(filter => {
        try {
          const filterMatched = filter.applyFilter(item, filter.value)
          if(filterMatched){
            filter.resultItems = [...filter.resultItems, item]
            filter.results = itemIndex + 1
          }
          return filterMatched
        } catch (error) {
          console.error(`Error applying filter ${filter.id}:`, error)
          return true
        }
      })
    });
  } catch (e) {
    console.error('Error applying filters:', e)
    return []
  }
}

/**
 * Update filter value and optionally persist it
 */
export function updateFilterValue<T extends FinInStoreFilter>(
  filters: T[],
  filterId: string,
  value: T['value'],
  persist = false
): T[] {
  const filterIndex = filters.findIndex(f => f.id === filterId)
  if (filterIndex === -1) return filters

  const updatedFilters = [...filters] as T[]
  const updatedFilter = {
    ...updatedFilters[filterIndex],
    value
  }

  updatedFilters[filterIndex] = updatedFilter

  // Persist to storage if needed
  if (persist && updatedFilter.storageKey) {
    localStorage.setItem(updatedFilter.storageKey, JSON.stringify(value))
  }

  return updatedFilters
}

/**
 * Get active filters
 */
export function getActiveFilters(filters: FinInStoreFilter[]): FinInStoreFilter[] {
  return filters.filter(filter => {
    if (filter.type === FilterType.BOOLEAN) {
      return filter.value
    } else if (filter.type === FilterType.MULTISELECT) {
      return Array.isArray(filter.value) && filter.value.length > 0
    }
    return false
  })
}

/**
 * Reset all filters to their default values
 */
export function resetFilters<T extends FinInStoreFilter>(filters: T[]): T[] {
  try {
    filters?.forEach(filter => {
      filter.storageKey && localStorage.removeItem(filter.storageKey)
    })
    return filters?.map(filter => ({
      ...filter,
      value: filter.defaultValue
    }))
  } catch (e) {
    console.error('Error resetting filters:', e)
    return filters
  }

}

/**
 * Get a filter by ID
 */
export function getFilterById<T extends FinInStoreFilter>(
  filters: T[],
  id: string
): T | undefined {
  return filters.find(filter => filter.id === id)
}

// Type guards
export function isBooleanFilter(filter: FinInStoreFilter): filter is BooleanFilter {
  return filter.type === FilterType.BOOLEAN
}

export function isMultiselectFilter(filter: FinInStoreFilter): filter is MultiselectFilter {
  return filter.type === FilterType.MULTISELECT
}

function getLabel(key: string, fallback: string): string {
  if (!i18n?.messages?.[i18n.locale]) return fallback
  return get(i18n.messages[i18n.locale], key, fallback)?.toString()
}

export const fetchPredictionsByAddress = async ({
  inputValue,
  predictionKeyTypes,
  countryCode,
}: {
  predictionKeyTypes: string[]
  countryCode?: string | string[]
  inputValue?: string
}): Promise<PlaceAutocompleteResponse> => {
  if (isEmpty(inputValue?.trim())) {
    return []
  }

  try {
    const regionCodes = countryCode
      ? (Array.isArray(countryCode) ? countryCode : [countryCode])
      : undefined

    const results = await fetchSuggestions({
      input: inputValue!,
      includedRegionCodes: regionCodes,
      includedPrimaryTypes: predictionKeyTypes?.length ? predictionKeyTypes : undefined,
    })

    const filteredPredictions = predictionKeyTypes?.length
      ? results.filter(prediction =>
          prediction.types.find(type => predictionKeyTypes.includes(type))
        )
      : results

    if (!filteredPredictions.length) {
      const error = {
        error: true,
        message: 'No predictions found matching the specified types',
        code: addressFinderErrorCodes.NO_PREDICTIONS_MATCHING_TYPES,
        inputValue: inputValue,
      }
      throw JSON.stringify(error)
    }

    return filteredPredictions
  } catch (e) {
    if (typeof e === 'string') throw e
    const error = {
      error: true,
      message: 'Error fetching predictions',
      code: 'FETCH_ERROR',
      inputValue: inputValue,
    }
    throw JSON.stringify(error)
  }
}


export const getPlacesByPredictionData = async ({
  prediction,
}: {
  prediction?: PlaceAutocompletePrediction
}): Promise<PlaceResult | null> => {
  if (!prediction) {
    throw new Error('no prediction value provided')
  }

  return await fetchPlaceDetails({
    placeId: prediction.place_id,
    fields: ['address_components', 'formatted_address', 'geometry', 'name'],
  })
}

export const formatGoogleAddress = (place: PlaceResult):FormattedAddress | null => {
  try {
    const zip: string =
      place.address_components?.find(component => component.types.includes('postal_code'))
        ?.long_name || ''
    const city: string =
      place.address_components?.find(component => component.types.some(currType => CITY_TYPES.includes(currType)))
        ?.long_name || ''
    const streetName: string =
      place.address_components?.find(component => component.types.includes('route'))?.long_name ||
      ''
    const streetNumber: string =
      place.address_components?.find(component => component.types.includes('street_number'))
        ?.long_name || ''
    const state_region: string =
      place.address_components?.find(component =>
        component.types.includes('administrative_area_level_1')
      )?.long_name || ''
    const country: string =
      place.address_components?.find(component => component.types.includes('country'))?.long_name ||
      ''
    const addressString: string =
      [streetNumber, streetName, city, state_region, country]
        .filter(string => !isEmpty(string))
        ?.reduce((acc, curr) => {
          if (curr) {
            acc.push(curr)
          }
          return acc
        }, [])
        .join(', ') || ''
    return {
      street1: `${streetNumber} ${streetName}`,
      city,
      state:
        place.address_components?.find(component =>
          component.types.includes('administrative_area_level_1')
        )?.short_name || '',
      country,
      zip,
      addressString,
      latitude:
        typeof place.geometry?.location?.lat === 'function'
          ? place.geometry.location.lat()
          : place.geometry?.location?.lat || '',
      longitude:
        typeof place.geometry?.location?.lng === 'function'
          ? place.geometry.location.lng()
          : place.geometry?.location?.lng || '',
    }
  } catch (e) {
    console.error('Error generating address data', e)
    return null
  }
}


